import React, { useCallback, useState, createContext, useContext, useMemo, useEffect } from 'react';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import { trackCodeChallenge } from './analytics-helpers';
import { startMfaChallenge, startStepUpAuth } from './api';
import { mapManagementApiTypeToAuthenticationApiChallengeType, TokenTimeoutError } from './constants';
import { messageSets } from './messages';
import { ChallengePhaseModalContent } from './phases/challenge';
import { ChoosePhaseModalContent } from './phases/choose';
import { PasswordPhaseModalContent } from './phases/password';
import style from './style.module.less';
import { ModalPortal } from '../../components/modal-portal';

/**
 * @typedef {import('./api').StartStepUpAuthRes} StartStepUpAuthRes
 * @typedef {import('./api').MfaEnrollment} MfaEnrollment
 * @typedef {import('./api').StartMfaChallengeRes} StartMfaChallengeRes
 * @typedef {import ('react-intl').MessageDescriptor} MessageDescriptor
 * @typedef {keyof typeof import('../../authentication/constants').PHONE_VERIFICATION_STATES} PhoneVerificationState
 */

const PHASES = {
  TRYING_WITHOUT_PASSWORD: 'TRYING_WITHOUT_PASSWORD',
  PASSWORD: 'PASSWORD',
  CHOOSE: 'CHOOSE',
  CHALLENGE: 'CHALLENGE',
  VERIFIED: 'VERIFIED',
  ERROR: 'ERROR',
};

/**
 * @typedef {object} StepUpAuthContextValue
 * @property {string} accessToken
 * @property {MfaEnrollment[]} mfaEnrollments
 * @property {() => void} detectAndHandleAuthTokenExpirationError
 */

/**
 * @type {React.Context<StepUpAuthContextValue>}
 */
export const StepUpAuthContext = createContext({ accessToken: '', mfaEnrollments: [] });

/**
 * @returns {StepUpAuthContextValue}
 */
export const useStepUpAuthContext = () => useContext(StepUpAuthContext);

/**
 * @param {object} props
 * @param {React.JSX.Element} [props.children] - pass children that should be gated behind completing step up
 * @param {string} props.source - request url that failed due to mfa required
 * @param {boolean} props.isModalOpen
 * @param {React.ReactNode | MessageDescriptor} props.stepUpAuthModalTitle title for the modal that should reference the workflow gated by this step-up auth
 * @param {Record<string, MessageDescriptor>} [props.messages]
 * @param {() => void} [props.onClose] callback for when user closes the modal
 * @param {(accessToken: string) => void} [props.onStepUpAuthSuccess]
 * @param {boolean} [props.isMfaSettings] - determines some of the properties on the token that's asked for during step up auth. If false then an auth token with an updated last_mfa_completed claim is asked for.
 * @param {boolean} [props.isUnskippable] - whether this step-up auth is in unskippable mode (shows additional context)
 * @param {PhoneVerificationState} [props.phoneVerificationState] - the current phone verification state for contextual messaging
 * @param {() => void} [props.onTokenTimeout] defaults to noop
 * @returns {React.JSX.Element}
 */
export const StepUpAuthContextProvider = ({
  children,
  source,
  isModalOpen,
  messages = messageSets.mfaSettingsMessages,
  onClose,
  onStepUpAuthSuccess,
  onTokenTimeout = noop,
  phoneVerificationState,
  stepUpAuthModalTitle,
  isMfaSettings = false,
  isUnskippable = false,
}) => {
  const [phase, setPhase] = useState(PHASES.TRYING_WITHOUT_PASSWORD);
  const [mfaEnrollments, setMfaEnrollments] = useState([]);
  const [mfaToken, setMfaToken] = useState('');
  const [oobCode, setOobCode] = useState('');

  const [challengedEnrollment, setChallengedEnrollment] = useState(null);

  const [accessToken, setAccessToken] = useState('');

  // Try passwordless step-up when modal opens
  useEffect(() => {
    if (phase === PHASES.TRYING_WITHOUT_PASSWORD && isModalOpen) {
      const attemptPasswordlessStepUp = async () => {
        try {
          const resData = await startStepUpAuth({ isMfaSettings, tryWithoutPassword: true });
          setMfaEnrollments(resData.enrollments);
          if (resData.isMfaRequired) {
            setPhase(PHASES.CHOOSE);
          } else {
            setPhase(PHASES.VERIFIED);
            setAccessToken(resData.mfaToken);
            onStepUpAuthSuccess?.(resData.mfaToken);
          }
          setMfaToken(resData.mfaToken);
        } catch {
          setPhase(PHASES.PASSWORD);
        }
      };
      attemptPasswordlessStepUp();
    }
  }, [phase, isModalOpen, isMfaSettings, onStepUpAuthSuccess]);

  const detectAndHandleAuthTokenExpirationError = useCallback(
    (error) => {
      const errObj = error?.response?.data || error;
      const errorCode = errObj?.code;
      const errorMessage = errObj?.message;

      const isAuthTokenExpirationError =
        errorCode === TokenTimeoutError.code ||
        Boolean(errorMessage?.match(new RegExp(TokenTimeoutError.message))) ||
        Boolean(errorMessage?.match(new RegExp(TokenTimeoutError.message2)));

      if (isAuthTokenExpirationError) {
        setMfaToken(null);
        setPhase(PHASES.TRYING_WITHOUT_PASSWORD);
        onTokenTimeout();
      }
    },
    [onTokenTimeout],
  );

  const contextValue = useMemo(() => {
    return {
      accessToken,
      detectAndHandleAuthTokenExpirationError,
      mfaEnrollments,
    };
  }, [accessToken, detectAndHandleAuthTokenExpirationError, mfaEnrollments]);

  /**
   * @param {StartStepUpAuthRes} resData
   */
  const onPasswordVerify = (resData) => {
    setMfaEnrollments(resData.enrollments);
    if (resData.isMfaRequired) {
      setPhase(PHASES.CHOOSE);
    } else {
      setPhase(PHASES.VERIFIED);
      setAccessToken(resData.mfaToken);
      onStepUpAuthSuccess?.(resData.mfaToken);
    }
    setMfaToken(resData.mfaToken);
  };

  /**
   * @param {MfaEnrollment} enrollment
   */
  const onChallengeEnrollment = async (enrollment) => {
    setChallengedEnrollment(enrollment);
    const resData = await startMfaChallenge({
      mfaToken,
      authenticatorId: enrollment.id,
      challengeType: mapManagementApiTypeToAuthenticationApiChallengeType[enrollment.type],
    });

    setPhase(PHASES.CHALLENGE);
    setMfaToken(resData.mfaToken);
    setOobCode(resData.oobCode);
    trackCodeChallenge({ source });
  };

  /**
   * @param {string} _accessToken
   */
  const onChallengePassed = (_accessToken) => {
    setAccessToken(_accessToken);
    setPhase(PHASES.VERIFIED);
    onStepUpAuthSuccess?.(_accessToken);
  };

  const onChooseOtherMethod = () => {
    setPhase(PHASES.CHOOSE);
    setChallengedEnrollment(null);
  };

  const onCloseModal = () => {
    // Prevent closing modal when in unskippable mode
    if (isUnskippable) {
      return;
    }
    onClose?.();
    setPhase(PHASES.TRYING_WITHOUT_PASSWORD);
  };

  const shouldShowModal = isModalOpen && phase !== PHASES.VERIFIED;

  return (
    <>
      {shouldShowModal ? (
        <ModalPortal hideCloseButton={isUnskippable} onClose={isUnskippable ? noop : onCloseModal}>
          <Modal size='medium'>
            <ModalHeader preventClose={isUnskippable}>
              <Text textType={TextType.Paragraph} value={stepUpAuthModalTitle} />
            </ModalHeader>
            {phase === PHASES.TRYING_WITHOUT_PASSWORD && (
              <ModalBody>
                <LoadingIndicator className={style.loading} />
              </ModalBody>
            )}
            {phase === PHASES.PASSWORD && (
              <PasswordPhaseModalContent
                isMfaSettings={isMfaSettings}
                isUnskippable={isUnskippable}
                messages={messages}
                onCancel={onCloseModal}
                onPasswordVerify={onPasswordVerify}
                phoneVerificationState={phoneVerificationState}
              />
            )}
            {phase === PHASES.CHOOSE && (
              <ChoosePhaseModalContent
                isUnskippable={isUnskippable}
                mfaEnrollments={mfaEnrollments}
                onCancel={onCloseModal}
                onChallengeEnrollment={onChallengeEnrollment}
                phoneVerificationState={phoneVerificationState}
              />
            )}
            {phase === PHASES.CHALLENGE && (
              <ChallengePhaseModalContent
                challengedEnrollment={challengedEnrollment}
                isMfaSettings={isMfaSettings}
                isUnskippable={isUnskippable}
                mfaToken={mfaToken}
                onCancel={onCloseModal}
                onChallengePassed={onChallengePassed}
                onChooseOtherMethod={isUnskippable ? onChooseOtherMethod : undefined}
                onResendCode={onChallengeEnrollment}
                oobCode={oobCode}
              />
            )}
          </Modal>
        </ModalPortal>
      ) : null}
      {phase === PHASES.VERIFIED && <StepUpAuthContext.Provider value={contextValue}>{children}</StepUpAuthContext.Provider>}
    </>
  );
};
